import { Dispatch, SetStateAction } from 'react';

import { Region, Territory } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { cloneDeep, find } from 'lodash';
import * as RateCalcUtil from 'src/components/RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import {
    autoCalculateFinalRate,
    calcMultiplierName,
    calculateRateCalcType,
    calculateRevCalcType,
    calculateServiceRevenue,
    getFullRateFor3PRate
} from 'src/components/RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import { PaperPlanRateGroup, RateCardCoreRateGridColumnName, RateMultiplierDisplayName } from 'src/constants';
import { CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP, CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP } from 'src/constants/rateCards';
import {
    IFirstPartyCorePlan,
    IFullRateGridRowData,
    IMultiplierType,
    IRateCardValues,
    IThirdPartyRateGridRowData
} from 'src/types/IRateCard';

function setThirdPartyRates(
    updatedThirdPartyRates: IThirdPartyRateGridRowData[],
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>
): void {
    setRateCardValues((prev) => ({
        ...prev,
        thirdPartyData: {
            ...prev.thirdPartyData,
            thirdPartyRates: updatedThirdPartyRates
        }
    }));
}

function set3PAndFullRates(
    updatedThirdPartyRates: IThirdPartyRateGridRowData[],
    fullRates: IFullRateGridRowData[],
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>
): void {
    setRateCardValues((prev) => ({
        ...prev,
        thirdPartyData: {
            ...prev.thirdPartyData,
            thirdPartyRates: updatedThirdPartyRates
        },
        fullRates: fullRates
    }));
}

export function copyCoreRatesTo3PGrid(
    rateCardValues: IRateCardValues,
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>,
    multiplierTypeList: IMultiplierType[],
    thirdPartyPlanList: Record<string, string | boolean | number>[],
    corePlanList: IFirstPartyCorePlan[],
    territoryMapping: Record<number, Territory>,
    regionMapping: Record<number, Region>
): void {
    let changed = false;

    const updatedThirdPartyRates: IThirdPartyRateGridRowData[] = [];
    const updatedFullRates: IFullRateGridRowData[] = cloneDeep(rateCardValues.fullRates).filter(
        (fullRate) =>
            ![PaperPlanRateGroup.CarrierIntro, PaperPlanRateGroup.CarrierStandard].includes(
                fullRate.rateGroup as PaperPlanRateGroup
            )
    );

    thirdPartyPlanList.forEach((thirdPartyPlan) => {
        let rowCopy: IThirdPartyRateGridRowData | undefined = undefined;
        const coreRatesColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(
            thirdPartyPlan['corePlan'].toString()
        );

        const territoryTerm = rateCardValues.coreRates.find(
            (territoryTerm) => territoryTerm[RateCardCoreRateGridColumnName.Territory] === thirdPartyPlan['territory']
        );

        const existingThirdPartyPlan = rateCardValues.thirdPartyData.thirdPartyRates.find(
            (thirdPartyRate) => thirdPartyRate.paperPlanName === thirdPartyPlan['paperPlanName'].toString()
        );

        if (typeof territoryTerm === 'undefined' && typeof existingThirdPartyPlan === 'undefined') {
            // Do nothing since there is no core rate and no existing 3p plan
        } else if (typeof territoryTerm === 'undefined' && typeof existingThirdPartyPlan !== 'undefined') {
            // Territory term is deleted so delete the respective 3P plans
            changed = true;
        } else if (typeof territoryTerm !== 'undefined' && typeof existingThirdPartyPlan === 'undefined') {
            // Territory term is added so add plans
            changed = true;
            rowCopy = {
                ...thirdPartyPlan
            } as IThirdPartyRateGridRowData;
            rowCopy.corePlanId = find(corePlanList, { corePlanDisplayName: rowCopy.corePlan })?.corePlanId ?? '0';
            const coreRatesColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(rowCopy.corePlan);

            // @ts-ignore
            rowCopy.corePlanPSM = territoryTerm[coreRatesColumnName];
            rowCopy.rateCardCurrency = territoryTerm[RateCardCoreRateGridColumnName.RatecardCurrency] || '';

            if (rateCardValues.contractTerms.thirdPartyDeductionCaps) {
                const overridePsmDiscountCap =
                    rateCardValues.contractTerms.thirdPartyDeductionCaps?.territoryOverrides?.find(
                        (territoryOverride) => territoryOverride.territory === rowCopy?.territory
                    );
                const psmDiscountCap = overridePsmDiscountCap
                    ? overridePsmDiscountCap.deductionCap
                    : rateCardValues.contractTerms.thirdPartyDeductionCaps?.globalDeductionCap;

                rowCopy.psmDiscCap = psmDiscountCap?.toString() ?? '';
            }

            rowCopy.revDiscCap = rateCardValues.contractTerms.thirdPartyDeductionCaps?.carrierBilling?.toString() ?? '';

            rowCopy.rateType = CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP.get(rowCopy.corePlan) as string;
            calculateRateCalcType(rowCopy);
            calcMultiplierName(rowCopy);
            rowCopy.multiplierTypeId = find(multiplierTypeList, {
                displayName: rowCopy.multiplierName
            })?.rateMultiplierTypeId;
            rowCopy.multiplierValue =
                // @ts-ignore
                rateCardValues.contractTerms.rateMultipliers[
                    find(multiplierTypeList, { displayName: rowCopy.multiplierName })?.contractTermMultiplierKey || ''
                ] ?? '';
            autoCalculateFinalRate(rowCopy);
            calculateRevCalcType(rowCopy);
            calculateServiceRevenue(rowCopy);
            updatedFullRates.push(getFullRateFor3PRate(rowCopy, territoryTerm, territoryMapping, regionMapping));
        } else if (
            typeof territoryTerm !== 'undefined' &&
            typeof existingThirdPartyPlan !== 'undefined' &&
            typeof coreRatesColumnName !== 'undefined'
        ) {
            // check if the rate changed
            const expectedCoreRate = territoryTerm[coreRatesColumnName] as string | undefined;

            rowCopy ??= cloneDeep(existingThirdPartyPlan);

            if (existingThirdPartyPlan.corePlanPSM !== expectedCoreRate) {
                changed = true;
                rowCopy.corePlanPSM = expectedCoreRate!.toString();
                if (rowCopy.corePlanPSM === '') {
                    calculateRateCalcType(rowCopy);
                }
                RateCalcUtil.autoCalculateFinalRate(rowCopy);
            }
            updatedFullRates.push(getFullRateFor3PRate(rowCopy, territoryTerm, territoryMapping, regionMapping));
        }

        if (typeof rowCopy !== 'undefined') {
            updatedThirdPartyRates.push(rowCopy);
        }
    });

    if (changed) {
        set3PAndFullRates(updatedThirdPartyRates, updatedFullRates, setRateCardValues);
    }
}

export function copyDiscountCapsTo3PGrid(
    rateCardValues: IRateCardValues,
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>
): void {
    let changed = false;

    const updatedThirdPartyRates = rateCardValues.thirdPartyData.thirdPartyRates.map((row) => {
        const territoryCap = find(rateCardValues.contractTerms.thirdPartyDeductionCaps?.territoryOverrides, {
            territory: row.territory
        })?.deductionCap;
        const globalCap = rateCardValues.contractTerms.thirdPartyDeductionCaps?.globalDeductionCap;
        const expectedValue = (territoryCap ?? globalCap)?.toString();

        const rowCopy: IThirdPartyRateGridRowData = cloneDeep(row);

        if (typeof expectedValue !== 'undefined' && row.psmDiscCap !== expectedValue) {
            changed = true;
            rowCopy.psmDiscCap = expectedValue;
            RateCalcUtil.calculateRateCalcType(rowCopy);
            RateCalcUtil.autoCalculateFinalRate(rowCopy);
        }

        const revDiscCap = rateCardValues.contractTerms.thirdPartyDeductionCaps?.carrierBilling?.toString();

        if (typeof revDiscCap !== 'undefined' && row.revDiscCap !== revDiscCap) {
            changed = true;
            rowCopy.revDiscCap = revDiscCap;
            RateCalcUtil.calculateRevCalcType(rowCopy);
            RateCalcUtil.calculateServiceRevenue(rowCopy);
        }

        return rowCopy;
    });

    if (changed) {
        setThirdPartyRates(updatedThirdPartyRates, setRateCardValues);
    }
}

type RowWithMultipliers = {
    multiplierName: string;
    multiplierValue: string;
};

function copyMultipliersToGrid<T extends RowWithMultipliers>(
    row: T,
    rateCardValues: IRateCardValues,
    multiplierTypeList: IMultiplierType[]
): [T, boolean] {
    const multiplierKey =
        find(multiplierTypeList, {
            displayName: row.multiplierName
        })?.contractTermMultiplierKey || '';

    // @ts-ignore
    const expectedValue: string = rateCardValues.contractTerms.rateMultipliers[multiplierKey]?.toString();

    if (typeof expectedValue === 'undefined' || row.multiplierValue === expectedValue) {
        return [row, false];
    } else {
        const rowCopy = cloneDeep(row);
        rowCopy.multiplierValue = expectedValue;
        return [rowCopy, true];
    }
}

export function copyMultipliersTo1PGrid(
    rateCardValues: IRateCardValues,
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>,
    multiplierTypeList: IMultiplierType[]
): void {
    let changed = false;

    // scan through all rows
    // find rows whose multiplier does not match
    const updatedFirstPartyRates = rateCardValues.firstPartyRates.map((row) => {
        const [newRow, rowChanged] = copyMultipliersToGrid(row, rateCardValues, multiplierTypeList);
        changed = changed || rowChanged;
        return newRow;
    });

    if (changed) {
        setRateCardValues((prev) => ({
            ...prev,
            firstPartyRates: updatedFirstPartyRates
        }));
    }
}

export function copyMultipliersTo3PGrid(
    rateCardValues: IRateCardValues,
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>,
    multiplierTypeList: IMultiplierType[]
): void {
    let changed = false;

    // scan through all rows
    // find rows whose multiplier does not match
    const updatedThirdPartyRates = rateCardValues.thirdPartyData.thirdPartyRates.map((row) => {
        const [newRow, rowChanged] = copyMultipliersToGrid(row, rateCardValues, multiplierTypeList);
        if (rowChanged) {
            changed = true;
            RateCalcUtil.autoCalculateFinalRate(newRow);
        }
        return newRow;
    });

    if (changed) {
        setThirdPartyRates(updatedThirdPartyRates, setRateCardValues);
    }
}
